import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { formatDateKey, formatWeekLabel, getISOWeekNumber } from '@/utils/planning';

/** Width of the pinned installer column (matches the grid's first <col>). */
const INSTALLER_COL_PX = 140;

/** Split Mon–Sat board days into weeks, keyed by the Monday's date key. */
export function chunkBoardWeeks(days: Date[]): { key: string; days: Date[] }[] {
  const weeks: { key: string; days: Date[] }[] = [];
  for (const day of days) {
    if (weeks.length === 0 || day.getDay() === 1) weeks.push({ key: formatDateKey(day), days: [] });
    weeks[weeks.length - 1].days.push(day);
  }
  return weeks;
}

/** Week label row above the day headers of the multi-week board. */
export function MultiWeekHeaderRow({ days, stickyHeadCls }: { days: Date[]; stickyHeadCls: string }) {
  return (
    <tr className="bg-gray-50 border-b border-gray-200">
      <th className={`${stickyHeadCls}border-r border-gray-200`} />
      {chunkBoardWeeks(days).map((week, i) => (
        <th
          key={week.key}
          colSpan={week.days.length}
          className={`px-3 py-1.5 text-xs font-bold text-td-navy text-left ${i > 0 ? 'border-l-[6px] border-l-gray-300' : ''}`}
        >
          <span className="inline-block sticky left-[152px]">
            {formatWeekLabel(getISOWeekNumber(week.days[0]), week.days)}
          </span>
        </th>
      ))}
    </tr>
  );
}

/** Last horizontal scroll position of the board. The grid remounts after every
 *  non-silent fetch (e.g. after creating a verlof), and that must not throw the
 *  user back to the selected week. Only one window is remembered: moving the
 *  window with the arrows always lands on the newly selected week. */
let savedScroll: { windowKey: string; left: number } | null = null;

/**
 * Scroll the multi-week board so `focusKey`'s column sits right after the pinned
 * installer column, or back to where it was if this same window just remounted.
 * No-op in single-week mode (`focusKey` null).
 */
export function useFocusWeekScroll(
  containerRef: RefObject<HTMLDivElement | null>,
  windowKey: string,
  focusKey: string | null,
  zoom: number,
  /** Width of the pinned column the week lines up behind (0 when the board has none). */
  leadPx = INSTALLER_COL_PX,
) {
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || !focusKey) return;
    if (savedScroll?.windowKey === windowKey) {
      el.scrollLeft = savedScroll.left;
    } else {
      const th = el.querySelector<HTMLElement>(`th[data-date-key="${focusKey}"]`);
      // The pinned column sticks to the scrollport's left edge; line the week up behind it.
      if (th) el.scrollLeft += th.getBoundingClientRect().left - el.getBoundingClientRect().left - leadPx * zoom;
    }
    savedScroll = { windowKey, left: el.scrollLeft };
    const onScroll = () => { savedScroll = { windowKey, left: el.scrollLeft }; };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [containerRef, windowKey, focusKey, zoom, leadPx]);
}

/**
 * Desktop scrollbar pinned to the bottom of the viewport for the multi-week
 * board. The board is taller than the screen, so its own scrollbar only shows
 * at the very end of the page; this one mirrors it and stays in view.
 */
export function StickyScrollbar({ targetRef }: { targetRef: RefObject<HTMLDivElement | null> }) {
  const barRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const target = targetRef.current;
    const bar = barRef.current;
    if (!target || !bar) return;
    const measure = () => setWidth(target.scrollWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(target);
    if (target.firstElementChild) ro.observe(target.firstElementChild);
    // Mirror one way at a time. Writing scrollLeft on one side fires a scroll event
    // on the other; echoing that back would cancel the browser's smooth wheel/
    // trackpad scroll animation every frame (felt as lag), so the echo is skipped.
    let skipBar = false;
    let skipTarget = false;
    const fromTarget = () => {
      if (skipTarget) { skipTarget = false; return; }
      if (bar.scrollLeft === target.scrollLeft) return;
      skipBar = true;
      bar.scrollLeft = target.scrollLeft;
      // If the write was clamped and fired no event, don't swallow the next real one.
      requestAnimationFrame(() => { skipBar = false; });
    };
    const fromBar = () => {
      if (skipBar) { skipBar = false; return; }
      if (target.scrollLeft === bar.scrollLeft) return;
      skipTarget = true;
      target.scrollLeft = bar.scrollLeft;
      requestAnimationFrame(() => { skipTarget = false; });
    };
    // A plain (vertical) mouse wheel over the bar scrolls the board sideways
    // instead of the page. Horizontal wheel/trackpad input keeps its native path.
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      e.preventDefault();
      const px = e.deltaMode === 1 ? e.deltaY * 40 : e.deltaMode === 2 ? e.deltaY * target.clientWidth : e.deltaY;
      target.scrollBy({ left: px });
    };
    fromTarget();
    target.addEventListener('scroll', fromTarget, { passive: true });
    bar.addEventListener('scroll', fromBar, { passive: true });
    bar.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      ro.disconnect();
      target.removeEventListener('scroll', fromTarget);
      bar.removeEventListener('scroll', fromBar);
      bar.removeEventListener('wheel', onWheel);
    };
  }, [targetRef]);

  // The first sync ran before the inner width existed (clamped to 0); re-sync once it does.
  useEffect(() => {
    if (barRef.current && targetRef.current) barRef.current.scrollLeft = targetRef.current.scrollLeft;
  }, [width, targetRef]);

  return (
    <div
      ref={barRef}
      className="hidden lg:block sticky bottom-0 z-30 mx-8 overflow-x-auto overflow-y-hidden bg-white/90 border-t border-gray-200"
      aria-hidden="true"
    >
      <div className="h-px" style={{ width }} />
    </div>
  );
}
